import { useMemo, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  Building2,
  CheckCircle2,
  PauseCircle,
  Star,
  Search,
  MapPin,
  Eye,
  Pencil,
  Pause,
  Play,
  Sparkles,
  ShieldAlert,
  X,
} from "lucide-react";

import { AdminShell } from "@/components/admin/admin-shell";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/admin/spaces/")({
  component: SpacesPage,
});

type Status = "Active" | "Inactive";

type Space = {
  id: string;
  name: string;
  location: string;
  city: string;
  merchant: string;
  category: string;
  rating: number | null;
  reviews: number | null;
  bookings: number;
  status: Status;
  featured?: boolean;
  cover: string;
};

const KSA_CITIES = ["Riyadh", "Jeddah", "Mecca", "Medina", "Dammam", "Khobar", "Dhahran", "Taif", "Tabuk", "Abha"];

const INITIAL: Space[] = [
  {
    id: "amber",
    name: "The Amber Lounge",
    location: "Al Olaya, Riyadh",
    city: "Riyadh",
    merchant: "Khalid A.",
    category: "Café & Lounge",
    rating: 4.8,
    reviews: 312,
    bookings: 1204,
    status: "Active",
    featured: true,
    cover: "https://images.unsplash.com/photo-1517457373958-b7bdd4587205?auto=format&fit=crop&w=200&q=60",
  },
  {
    id: "bloom",
    name: "Studio Bloom",
    location: "Al Andalus, Jeddah",
    city: "Jeddah",
    merchant: "Sarah M.",
    category: "Creative Studio",
    rating: 4.6,
    reviews: 189,
    bookings: 893,
    status: "Active",
    cover: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=200&q=60",
  },
  {
    id: "zenith",
    name: "Zenith Cowork Hub",
    location: "Al Faisaliyah, Riyadh",
    city: "Riyadh",
    merchant: "Wei T.",
    category: "Co-working",
    rating: 4.4,
    reviews: 76,
    bookings: 512,
    status: "Active",
    cover: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=200&q=60",
  },
  {
    id: "sky",
    name: "Sky Terrace Bar",
    location: "Corniche, Khobar",
    city: "Khobar",
    merchant: "James O.",
    category: "Rooftop Bar",
    rating: 3.1,
    reviews: 54,
    bookings: 287,
    status: "Inactive",
    cover: "https://images.unsplash.com/photo-1470337458703-46ad1756a187?auto=format&fit=crop&w=200&q=60",
  },
  {
    id: "cove",
    name: "Cove Photography",
    location: "Al Zahra, Jeddah",
    city: "Jeddah",
    merchant: "Omar K.",
    category: "Photography",
    rating: 4.9,
    reviews: 421,
    bookings: 2108,
    status: "Active",
    featured: true,
    cover: "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=200&q=60",
  },
  {
    id: "grand",
    name: "Grand Event Hall",
    location: "Al Faisal, Mecca",
    city: "Mecca",
    merchant: "Priya R.",
    category: "Event Venue",
    rating: 4.3,
    reviews: 97,
    bookings: 456,
    status: "Inactive",
    cover: "https://images.unsplash.com/photo-1519167758481-83f550bb49b3?auto=format&fit=crop&w=200&q=60",
  },
  {
    id: "velvet",
    name: "Velvet Garden",
    location: "Al Shifa, Abha",
    city: "Abha",
    merchant: "Lucia V.",
    category: "Garden",
    rating: 4.7,
    reviews: 234,
    bookings: 1567,
    status: "Active",
    cover: "https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?auto=format&fit=crop&w=200&q=60",
  },
  {
    id: "pixel",
    name: "Pixel Loft Tech Hub",
    location: "Al Faisaliyah, Dammam",
    city: "Dammam",
    merchant: "Tom B.",
    category: "Tech Hub",
    rating: 4.2,
    reviews: 41,
    bookings: 198,
    status: "Active",
    cover: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=200&q=60",
  },
];

const CATEGORY_BREAKDOWN = [
  { name: "Café & Lounge", spaces: 94, pct: 27, color: "bg-primary" },
  { name: "Co-working", spaces: 78, pct: 22, color: "bg-emerald-500" },
  { name: "Creative Studio", spaces: 61, pct: 18, color: "bg-amber-500" },
  { name: "Event Venue", spaces: 52, pct: 15, color: "bg-blue-500" },
  { name: "Photography Studio", spaces: 38, pct: 11, color: "bg-purple-500" },
  { name: "Other", spaces: 25, pct: 7, color: "bg-slate-400" },
];

const TABS = ["All", "Active", "Inactive", "Featured"] as const;
type Tab = (typeof TABS)[number];

function statusPill(status: Status) {
  return status === "Active"
    ? "bg-emerald-500/10 text-emerald-700 ring-emerald-500/20"
    : "bg-slate-500/10 text-slate-700 ring-slate-500/20";
}

function SpacesPage() {
  const { t } = useTranslation();
  const [spaces, setSpaces] = useState<Space[]>(INITIAL);
  const [tab, setTab] = useState<Tab>("All");
  const [query, setQuery] = useState("");
  const [city, setCity] = useState<string>("all");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const cities = KSA_CITIES;

  const filtered = useMemo(() => {
    return spaces.filter((s) => {
      if (tab === "Featured" && !s.featured) return false;
      if (tab === "Active" && s.status !== "Active") return false;
      if (tab === "Inactive" && s.status !== "Inactive") return false;
      if (city !== "all" && s.city !== city) return false;
      if (query) {
        const q = query.toLowerCase();
        if (
          !s.name.toLowerCase().includes(q) &&
          !s.location.toLowerCase().includes(q) &&
          !s.merchant.toLowerCase().includes(q) &&
          !s.category.toLowerCase().includes(q)
        )
          return false;
      }
      return true;
    });
  }, [spaces, tab, query, city]);

  const counts = useMemo(
    () => ({
      All: spaces.length,
      Active: spaces.filter((s) => s.status === "Active").length,
      Inactive: spaces.filter((s) => s.status === "Inactive").length,
      Featured: spaces.filter((s) => s.featured).length,
    }),
    [spaces],
  );

  const kpis = [
    {
      label: t("adminSpaces.kpi.total"),
      value: counts.All,
      hint: t("adminSpaces.kpi.totalHint"),
      hintClass: "text-muted-foreground",
      icon: Building2,
      iconWrap: "bg-primary/10 text-primary",
    },
    {
      label: t("adminSpaces.kpi.active"),
      value: counts.Active,
      hint: t("adminSpaces.kpi.activeHint", {
        pct: Math.round((counts.Active / counts.All) * 100),
      }),
      hintClass: "text-emerald-600",
      icon: CheckCircle2,
      iconWrap: "bg-emerald-500/10 text-emerald-600",
    },
    {
      label: t("adminSpaces.kpi.inactive"),
      value: counts.Inactive,
      hint: t("adminSpaces.kpi.inactiveHint"),
      hintClass: "text-slate-500",
      icon: PauseCircle,
      iconWrap: "bg-slate-500/10 text-slate-600",
    },
    {
      label: t("adminSpaces.kpi.featured"),
      value: counts.Featured,
      hint: t("adminSpaces.kpi.featuredHint"),
      hintClass: "text-[#5958F3]",
      icon: Sparkles,
      iconWrap: "bg-[#5958F3]/10 text-[#5958F3]",
    },
  ];

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (filtered.every((s) => selected.has(s.id)) && filtered.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((s) => s.id)));
    }
  };

  const setStatus = (ids: string[], status: Status) =>
    setSpaces((prev) => prev.map((s) => (ids.includes(s.id) ? { ...s, status } : s)));
  const toggleFeatured = (ids: string[]) =>
    setSpaces((prev) => prev.map((s) => (ids.includes(s.id) ? { ...s, featured: !s.featured } : s)));

  const clearFilters = () => {
    setQuery("");
    setCity("all");
    setTab("All");
  };
  const hasFilters = query !== "" || city !== "all" || tab !== "All";

  return (
    <AdminShell
      title={t("adminSpaces.title")}
      subtitle={t("adminSpaces.subtitle")}
    >
      {/* KPIs */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {kpis.map((k) => (
          <Card
            key={k.label}
            className="rounded-2xl border-border/60 p-5 shadow-soft transition hover:-translate-y-0.5 hover:shadow-card"
          >
            <div className="flex items-start gap-4">
              <div className={cn("grid h-11 w-11 shrink-0 place-items-center rounded-xl", k.iconWrap)}>
                <k.icon className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="font-display text-3xl font-bold tracking-tight">{k.value}</p>
                <p className="mt-0.5 text-sm font-medium text-foreground">{k.label}</p>
                <p className={cn("mt-1 text-xs font-medium", k.hintClass)}>{k.hint}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Toolbar */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[240px] flex-1">
          <Search className="pointer-events-none absolute start-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("adminSpaces.search")}
            className="h-10 rounded-xl border-border/70 bg-background ps-10"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="absolute end-2 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-md text-muted-foreground hover:bg-muted"
              aria-label={t("adminSpaces.clearSearch")}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {TABS.map((tabKey) => {
            const active = tab === tabKey;
            return (
              <button
                key={tabKey}
                onClick={() => setTab(tabKey)}
                className={cn(
                  "inline-flex h-9 items-center gap-1.5 rounded-full border px-3.5 text-xs font-semibold transition",
                  active
                    ? "border-transparent text-white shadow-soft"
                    : "border-border/60 bg-background text-muted-foreground hover:text-foreground",
                )}
                style={active ? { backgroundColor: "#5958F3" } : undefined}
              >
                {t(`adminSpaces.tabs.${tabKey}`)}
                <span
                  className={cn(
                    "rounded-full px-1.5 py-0.5 text-[10px] font-semibold",
                    active ? "bg-white/20" : "bg-muted",
                  )}
                >
                  {counts[tabKey]}
                </span>
              </button>
            );
          })}
        </div>
        <div className="ms-auto flex items-center gap-2">
          <Select value={city} onValueChange={setCity}>
            <SelectTrigger className="h-10 w-[150px] rounded-xl">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <SelectValue placeholder={t("adminSpaces.city")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("adminSpaces.allCities")}</SelectItem>
              {cities.map((c) => (
                <SelectItem key={c} value={c}>
                  {t(`adminSpaces.cities.${c}`, { defaultValue: c })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="h-10 rounded-xl text-muted-foreground">
              {t("adminSpaces.clear")}
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <Card className="mt-4 overflow-hidden rounded-2xl border-border/60 shadow-soft">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 px-5 py-4">
          <div>
            <h2 className="font-display text-lg font-bold tracking-tight">{t("adminSpaces.table.title")}</h2>
            <p className="text-xs text-muted-foreground">
              {t("adminSpaces.table.showing", { shown: filtered.length, total: spaces.length })}
              {selected.size > 0 &&
                ` · ${t("adminSpaces.table.selected", { count: selected.size })}`}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={selected.size === 0}
              onClick={() => {
                toggleFeatured(Array.from(selected));
                setSelected(new Set());
              }}
              className="h-9 rounded-xl"
            >
              <Star className="h-4 w-4" /> {t("adminSpaces.table.toggleFeature")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={selected.size === 0}
              onClick={() => {
                setStatus(Array.from(selected), "Inactive");
                setSelected(new Set());
              }}
              className="h-9 rounded-xl border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700 disabled:opacity-50"
            >
              <ShieldAlert className="h-4 w-4" /> {t("adminSpaces.table.bulkPause")}
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/60 bg-surface/60 text-[11px] uppercase tracking-wider text-muted-foreground">
                <th className="w-10 py-3 ps-5">
                  <Checkbox
                    checked={filtered.length > 0 && filtered.every((s) => selected.has(s.id))}
                    onCheckedChange={toggleAll}
                    aria-label={t("adminSpaces.table.selectAll")}
                  />
                </th>
                <th className="py-3 text-start font-semibold">{t("adminSpaces.table.space")}</th>
                <th className="py-3 text-start font-semibold">{t("adminSpaces.table.merchant")}</th>
                <th className="py-3 text-start font-semibold">{t("adminSpaces.table.category")}</th>
                <th className="py-3 text-start font-semibold">{t("adminSpaces.table.rating")}</th>
                <th className="py-3 text-start font-semibold">{t("adminSpaces.table.bookings")}</th>
                <th className="py-3 text-start font-semibold">{t("adminSpaces.table.status")}</th>
                <th className="py-3 pe-5 text-end font-semibold">{t("adminSpaces.table.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => {
                const isSelected = selected.has(s.id);
                return (
                  <tr
                    key={s.id}
                    className={cn(
                      "border-b border-border/60 transition hover:bg-surface/60",
                      isSelected && "bg-[#5958F3]/5",
                    )}
                  >
                    <td className="ps-5 pe-4">
                      <Checkbox checked={isSelected} onCheckedChange={() => toggle(s.id)} />
                    </td>
                    <td className="py-4">
                      <div className="flex items-center gap-3">
                        <img
                          src={s.cover}
                          alt={s.name}
                          className="h-11 w-11 shrink-0 rounded-xl object-cover"
                          loading="lazy"
                decoding="async"
                        />
                        <div className="min-w-0">
                          <p className="truncate font-semibold text-foreground">{s.name}</p>
                          <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-muted-foreground">
                            <MapPin className="h-3 w-3" /> {s.location}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4">
                      <div className="flex items-center gap-2">
                        <Avatar className="h-7 w-7">
                          <AvatarFallback className="text-[10px]">
                            {s.merchant
                              .split(" ")
                              .map((p) => p[0])
                              .join("")}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-xs font-medium text-muted-foreground">{s.merchant}</span>
                      </div>
                    </td>
                    <td className="py-4">
                      <span className="inline-flex items-center rounded-lg bg-muted/60 px-2.5 py-1 text-xs font-medium text-foreground">
                        {t(`adminSpaces.categoryNames.${s.category}`, { defaultValue: s.category })}
                      </span>
                    </td>
                    <td className="py-4">
                      {s.rating ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-foreground">
                          <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                          {s.rating} <span className="font-normal text-muted-foreground">({s.reviews})</span>
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">{t("adminSpaces.table.noReviews")}</span>
                      )}
                    </td>
                    <td className="py-4 text-sm font-semibold text-foreground">{s.bookings.toLocaleString()}</td>
                    <td className="py-4">
                      <div className="flex flex-col items-start gap-1">
                        <span
                          className={cn(
                            "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset",
                            statusPill(s.status),
                          )}
                        >
                          <span
                            className={cn(
                              "h-1.5 w-1.5 rounded-full",
                              s.status === "Active" ? "bg-emerald-500" : "bg-slate-400",
                            )}
                          />
                          {t(`adminSpaces.status.${s.status}`)}
                        </span>
                        {s.featured && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-[#5958F3]/10 px-2 py-0.5 text-[10px] font-semibold text-[#5958F3]">
                            <Sparkles className="h-2.5 w-2.5" /> {t("adminSpaces.featuredBadge")}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-4 pe-5">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          asChild
                          size="icon"
                          variant="ghost"
                          title={t("adminSpaces.view")}
                          className="h-8 w-8 rounded-lg text-muted-foreground hover:text-foreground"
                        >
                          <Link to="/admin/spaces/$id" params={{ id: s.id }}>
                            <Eye className="h-4 w-4" />
                          </Link>
                        </Button>

                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setStatus([s.id], s.status === "Active" ? "Inactive" : "Active")}
                          title={s.status === "Active" ? t("adminSpaces.pause") : t("adminSpaces.activate")}
                          className={cn(
                            "h-8 w-8 rounded-lg",
                            s.status === "Active"
                              ? "bg-amber-500/10 text-amber-600 hover:bg-amber-500/20"
                              : "bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20",
                          )}
                        >
                          {s.status === "Active" ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-16 text-center">
                    <p className="text-sm font-semibold">{t("adminSpaces.table.empty")}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{t("adminSpaces.table.emptyHint")}</p>
                    {hasFilters && (
                      <Button variant="outline" size="sm" onClick={clearFilters} className="mt-4 rounded-xl">
                        {t("adminSpaces.table.clearFilters")}
                      </Button>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border/60 px-5 py-4">
          <p className="text-xs text-muted-foreground">
            {t("adminSpaces.table.showing", { shown: filtered.length, total: spaces.length })}
          </p>
          <div className="flex items-center gap-1">
            <Button size="icon" variant="outline" className="h-8 w-8 rounded-lg" disabled>
              ‹
            </Button>
            <Button
              size="icon"
              className="h-8 w-8 rounded-lg text-xs text-white"
              style={{ backgroundColor: "#5958F3" }}
            >
              1
            </Button>
            <Button size="icon" variant="outline" className="h-8 w-8 rounded-lg" disabled>
              ›
            </Button>
          </div>
        </div>
      </Card>

      {/* Bottom analytics */}
      <div className="mt-6 grid gap-4 lg:grid-cols-[1.7fr_1fr]">
        <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
          <div className="mb-5 flex items-center justify-between">
            <h3 className="font-display text-lg font-bold tracking-tight">{t("adminSpaces.byCategory.title")}</h3>
            <span className="text-xs text-muted-foreground">
              {t("adminSpaces.byCategory.total", {
                count: CATEGORY_BREAKDOWN.reduce((a, c) => a + c.spaces, 0),
              })}
            </span>
          </div>
          <div className="space-y-4">
            {CATEGORY_BREAKDOWN.map((c) => (
              <div key={c.name}>
                <div className="mb-1.5 flex items-center justify-between text-sm">
                  <span className="font-medium text-foreground">
                    {t(`adminSpaces.categoryNames.${c.name}`, { defaultValue: c.name })}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {t("adminSpaces.byCategory.detail", { count: c.spaces, pct: c.pct })}
                  </span>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className={cn("h-full rounded-full transition-all", c.color)}
                    style={{ width: `${c.pct * 3.4}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>

        <div className="space-y-4">
          <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-display text-base font-bold tracking-tight">{t("adminSpaces.topRated")}</h3>
            </div>
            <ul className="space-y-3">
              {[...spaces]
                .filter((s) => s.rating)
                .sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0))
                .slice(0, 3)
                .map((r, i) => (
                  <li key={r.id} className="flex items-center gap-3">
                    <span
                      className={cn(
                        "grid h-7 w-7 shrink-0 place-items-center rounded-full text-[11px] font-bold text-white",
                        i === 0 ? "bg-amber-400" : i === 1 ? "bg-slate-300" : "bg-amber-700/70",
                      )}
                    >
                      {i + 1}
                    </span>
                    <span className="flex-1 truncate text-sm font-medium">{r.name}</span>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold">
                      <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                      {r.rating}
                    </span>
                  </li>
                ))}
            </ul>
          </Card>

          <Card className="rounded-2xl border-none p-5 text-white shadow-soft" style={{ backgroundColor: "#5958F3" }}>
            <h3 className="font-display text-base font-bold tracking-tight">{t("adminSpaces.coverage.title")}</h3>
            <div className="mt-4 grid grid-cols-2 gap-3">
              {[
                { v: "12", l: t("adminSpaces.coverage.cities") },
                { v: "6", l: t("adminSpaces.coverage.countries") },
                { v: "4.6", l: t("adminSpaces.coverage.avgRating") },
                { v: "89%", l: t("adminSpaces.coverage.occupancy") },
              ].map((s) => (
                <div key={s.l} className="rounded-xl bg-white/10 p-3">
                  <p className="font-display text-2xl font-bold">{s.v}</p>
                  <p className="text-[11px] text-white/80">{s.l}</p>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </AdminShell>
  );
}
